<template>
  <div class="menu-wrapper d-flex align-items-center">
    <button class="navbar-toggler" type="button" data-bs-toggle="offcanvas" data-bs-target="#offcanvasNavbar"
            aria-controls="offcanvasNavbar" aria-label="Toggle navigation">
      <span class="navbar-toggler-icon"></span>
    </button>

    <div class="offcanvas offcanvas-end" tabindex="-1" id="offcanvasNavbar" aria-labelledby="offcanvasNavbarLabel">
      <div class="offcanvas-header">
        <div class="d-flex align-items-center gap-3" @click.prevent="navigateTo('/profile')" style="cursor: pointer;">
          <img
              :src="profileImage"
              class="rounded-circle"
              style="width: 50px; height: 50px; object-fit: cover;"
          />
          <div>
            <h6 class="mb-0">{{ user?.name }}</h6>
            <small class="text-muted" v-if="user && Array.isArray(user.roles) && user.roles.length">
              {{ user.roles.map(r => r.name).join(' - ') }}
            </small>
          </div>
        </div>

        <button class="btn-close text-reset" data-bs-dismiss="offcanvas" aria-label="Close"></button>
      </div>

      <div class="offcanvas-body">
        <div class="mb-3">
          <select
              v-model="$i18n.locale"
              class="form-select"
              @change="changeLanguage"
          >
            <option v-for="lang in supportedLanguages" :key="lang.code" :value="lang.code">
              {{ lang.label }}
            </option>
          </select>

        </div>

        <hr />

        <ul class="navbar-nav">
          <li class="nav-item">
            <a
                class="nav-link"
                :class="{ active: $route.path === '/trickList' }"
                @click.prevent="navigateTo('/trickList')"
            >
              {{ $t('menu.trick_list') }}
            </a>
          </li>
          <li class="nav-item">
            <a
                class="nav-link"
                :class="{ active: $route.path === '/rulebooks' }"
                @click.prevent="navigateTo('/rulebooks')"
            >
              {{ $t('menu.rulebooks') }}
            </a>
          </li>
          <li v-if="canAccessTrainingZone" class="nav-item">
            <a
                class="nav-link"
                :class="{ active: $route.path === '/training' }"
                @click.prevent="navigateTo('/training')"
            >
              {{ $t('menu.training') }}
            </a>
          </li>
          <li class="nav-item">
            <a
                class="nav-link"
                :class="{ active: $route.path === '/competitions' }"
                @click.prevent="navigateTo('/competitions')"
            >
              {{ $t('menu.competitions') }}
            </a>
          </li>
          <li class="nav-item">
            <a
                class="nav-link"
                href="#"
                @click.prevent="showCommunityDropdown = !showCommunityDropdown"
            >
              {{ $t('menu.community') }}
              <i :class="showCommunityDropdown ? 'fa fa-chevron-up' : 'fa fa-chevron-down'" class="ms-1"></i>
            </a>
            <ul v-if="showCommunityDropdown" class="list-unstyled ps-3">
              <li>
                <a
                    class="nav-link"
                    :class="{ active: $route.path === '/athletes' }"
                    @click.prevent="navigateTo('/athletes')"
                >
                  {{ $t('menu.athletes') }}
                </a>
              </li>
              <li>
                <a
                    class="nav-link"
                    :class="{ active: $route.path === '/judges' }"
                    @click.prevent="navigateTo('/judges')"
                >
                  {{ $t('menu.judges') }}
                </a>
              </li>
              <li>
                <a
                    class="nav-link"
                    :class="{ active: $route.path === '/organizer' }"
                    @click.prevent="navigateTo('/organizer')"
                >
                  {{ $t('menu.organizer') }}
                </a>
              </li>
            </ul>
          </li>
          <li class="nav-item" v-if="isSupervisorUser">
            <a
                class="nav-link"
                :class="{ active: $route.path === '/supervisor/management' }"
                @click.prevent="navigateTo('/supervisor/management')"
            >
              {{ $t('menu.management') || 'Management' }}
            </a>
          </li>
          <li class="nav-item d-flex align-items-center">
            <span class="nav-link disabled" style="cursor: not-allowed; opacity: 0.6;">
              {{ $t('menu.world_ranking') }}
              <small class="text-muted ms-2">&larr; {{ $t('menu.soon') }}</small>
            </span>
          </li>
        </ul>

        <hr />

        <button class="btn btn-outline-danger w-100 d-flex align-items-center justify-content-center gap-2"
                @click="logoutAndRedirect">
          <i class="fa-solid fa-right-from-bracket"></i>
          {{ $t('menu.logout') }}
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import { Offcanvas } from 'bootstrap';
import { mapActions, mapState } from 'vuex';
import { supportedLanguages } from '@/constants/languages';
import router from '@/router/router';
import { isAthlete, isJudge, isSupervisor } from '@/helpers/roleUtils';

export default {
  name: 'NavbarMenu',
  data() {
    return {
      supportedLanguages,
      showCommunityDropdown: false
    };
  },
  computed: {
    ...mapState('user', ['user']),
    profileImage() {
      return this.user?.img_path || require('@/assets/user.png');
    },
    isSupervisorUser() {
      return isSupervisor(this.user);
    },
    canAccessTrainingZone() {
      return isAthlete(this.user) || isJudge(this.user) || isSupervisor(this.user);
    }
  },
  methods: {
    ...mapActions('user', ['setLogout']),
    changeLanguage() {
      localStorage.setItem('lang', this.$i18n.locale);
    },
    async logoutAndRedirect() {
      await this.setLogout();
      await router.push('/');
    },
    navigateTo(path) {
      const offcanvasEl = document.getElementById('offcanvasNavbar');
      const offcanvasInstance = Offcanvas.getInstance(offcanvasEl);

      if (offcanvasInstance) {
        offcanvasInstance.hide();

        setTimeout(() => {
          const backdrops = document.querySelectorAll('.offcanvas-backdrop');
          backdrops.forEach(el => el.remove());
          document.body.style.overflow = '';
        }, 300);
      }

      if (router.path !== path) {
        this.showCommunityDropdown = false;
        router.push(path);
      }
    }
  },
  mounted() {
    const savedLang = localStorage.getItem('lang');
    const userLang = this.user?.language;
    const langToUse = userLang || savedLang;

    if (langToUse && supportedLanguages.map(l => l.code).includes(langToUse)) {
      this.$i18n.locale = langToUse;
    }

    const offcanvasEl = document.getElementById('offcanvasNavbar');
    if (offcanvasEl) {
      offcanvasEl.addEventListener('hidden.bs.offcanvas', () => {
        const backdrops = document.querySelectorAll('.offcanvas-backdrop');
        backdrops.forEach(el => el.remove());
        document.body.style.overflow = '';
        document.body.classList.remove('offcanvas-open');
      });
    }
  }
};
</script>

<style scoped>
.offcanvas {
  background-color: #f8f9fa;
}
.nav-link {
  cursor: pointer;
}
</style>
